import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const pad2 = (n) => String(n).padStart(2, "0");

const getLocalDateKey = (d) => {
  try {
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  } catch (e) {
    return "";
  }
};

const getLocalDateTimeKeyMinute = (d) => {
  try {
    return `${getLocalDateKey(d)}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  } catch (e) {
    return "";
  }
};

const buildSchedDateTime = (dateYYYYMMDD, timeHHMM) => {
  if (!dateYYYYMMDD || !timeHHMM) return "";
  // Backend calls elsewhere expect an ISO-like shape: YYYY-MM-DDTHH:mm:ss
  return `${dateYYYYMMDD}T${timeHHMM}:00`;
};

const normalizeSchedDateTime = (value) => {
  if (!value) return "";
  const s = String(value).trim().replace(" ", "T");
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(s)) return s;
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(s)) return `${s}:00`;
  const d = new Date(value);
  if (!isNaN(d)) return d.toISOString().slice(0, 19);
  return s;
};

const buildPostBookingScheduleEnvelope = ({ masterId, customerId, schedDateTime, updateBy }) => {
  const masterNumeric = Number(masterId) || 0;
  const customerNumeric = Number(customerId) || 0;
  const sched = normalizeSchedDateTime(schedDateTime);
  return {
    PREFIX: "PB",
    PAY_MODE: "0",
    UPDATE_BY: updateBy || "",
    PROD_ITEM: [
      {
        FSMasterID: masterNumeric,
        CustomerId: customerNumeric,
        pid: masterNumeric,
        Quantity: 1,
        Amount: 0,
        DESC_ENG: `Master: ${masterNumeric}`,
        ReserveStatus: 0,
        TYPE: 0,
      },
      {
        FSMasterID: masterNumeric,
        CustomerId: customerNumeric,
        pid: masterNumeric,
        Quantity: 1,
        Amount: 0,
        DESC_ENG: `Booking Schedule: ${sched}`,
        SchedDate: sched,
        ReserveStatus: 0,
        TYPE: 1,
      },
    ],
  };
};

const generateHalfHourTimes = (startHourInclusive = 10, endHourExclusive = 20) => {
  const results = [];
  for (let h = startHourInclusive; h < endHourExclusive; h++) {
    for (const m of [0, 30]) {
      results.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
  }
  return results;
};

const AppointmentPage = () => {
  const [appointments, setAppointments] = useState([]);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [selectedGroupKey, setSelectedGroupKey] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newDate, setNewDate] = useState("");
  const [newTime, setNewTime] = useState("");
  const [buid, setBUID] = useState("");
  const [recDate, setRecDate] = useState("");
  const [filterDate, setFilterDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [timeFilter, setTimeFilter] = useState("");
  const navigate = useNavigate();

  const todayDateString = new Date().toISOString().split("T")[0];

  const userID = localStorage.getItem("userID");
  const [channelName, setChannelName] = useState("");
  const [masCode, setMasCode] = useState("");
  const [profiles, setProfiles] = useState([]);
  const [isNarrowScreen, setIsNarrowScreen] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsNarrowScreen(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    // Fetch master profiles and keep the list in state. We'll resolve per-appointment when needed.
    const fetchProfiles = async () => {
      try {
        const res = await fetch(
          "https://xianhefairy.com/LotusFairy_API/api/profile/GetAll"
        );
        if (!res.ok) {
          throw new Error(`Failed to fetch profiles: ${res.status}`);
        }
        const data = await res.json();
        console.log("Fetched profiles:", data);
        setProfiles(data || []);

        // If the logged-in user is a master present in the list, set defaults for channel/masCode
        const numericUserId = Number(userID);
        const matched = (data || []).find((p) => Number(p.ID) === numericUserId);
        if (matched) {
          setChannelName(matched.DisplayName || matched.FirstName || "");
          setMasCode(matched.EmpCode || "");

          // If the profile includes an assigned date, use it as the filterDate (format to YYYY-MM-DD)
          const dateFields = [
            "AssignedDate",
            "Assigned_Date",
            "AssignedOn",
            "DateAssigned",
            "AssignDate",
            "Assigned",
            "assignedDate",
            "assigned_on",
          ];
          for (const f of dateFields) {
            if (matched[f]) {
              const d = new Date(matched[f]);
              if (!isNaN(d)) {
                setFilterDate(d.toISOString().split("T")[0]);
              }
              break;
            }
          }
        }
      } catch (error) {
        console.error("Error fetching master profiles:", error);
        setProfiles([]);
      }
    };

    fetchProfiles();
  }, [userID]);

  useEffect(() => {
    const fetchAppointments = async () => {
      try {
        const response = await fetch(
          `https://xianhefairy.com/LotusFairy_API/api/booking/getMasterCheckCall?ID=${userID}`
        );
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        const apiData = await response.json();
        setAppointments(apiData);
      } catch (error) {
        console.error("Failed to fetch appointments:", error);
      }
    };

    fetchAppointments();
  }, []);

  const handleAppointmentClick = (group) => {
    const nextKey = group?.key || null;
    const willSelect = selectedGroupKey !== nextKey;
    setSelectedGroupKey(willSelect ? nextKey : null);
    setSelectedGroup(willSelect ? group : null);

    // Keep existing single-value state for backward compatibility flows
    const first = (group?.items && group.items[0]) || null;
    setSelectedAppointment(willSelect ? nextKey : null);
    setBUID(willSelect ? first?.UID : "");
    setRecDate(willSelect ? first?.REC_DATE : "");
    setCustID(willSelect ? first?.CUST_ID : undefined);
  };

  const handleChangeAppointment = (appointment) => {
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setNewDate("");
    setNewTime("");
  };

  const convertTo24HourFormat = (time12h) => {
    const [time, modifier] = time12h.split(" ");
    let [hours, minutes] = time.split(":");

    if (modifier === "PM" && hours !== "12") {
      hours = String(parseInt(hours, 10) + 12);
    } else if (modifier === "AM" && hours === "12") {
      hours = "00";
    }

    return `${hours}:${minutes}:00`; // Ensures HH:mm:ss format
  };

  const timeSlots = [
    { start: { hour: 10, minute: 0 }, end: { hour: 10, minute: 30 } },
    { start: { hour: 10, minute: 30 }, end: { hour: 11, minute: 0 } },
    { start: { hour: 11, minute: 0 }, end: { hour: 11, minute: 30 } },
    { start: { hour: 11, minute: 30 }, end: { hour: 12, minute: 0 } },
    { start: { hour: 12, minute: 0 }, end: { hour: 12, minute: 30 } },
    { start: { hour: 12, minute: 30 }, end: { hour: 13, minute: 0 } },
    { start: { hour: 13, minute: 0 }, end: { hour: 13, minute: 30 } },
    { start: { hour: 13, minute: 30 }, end: { hour: 14, minute: 0 } },
    { start: { hour: 14, minute: 0 }, end: { hour: 14, minute: 30 } },
    { start: { hour: 14, minute: 30 }, end: { hour: 15, minute: 0 } },
    { start: { hour: 15, minute: 0 }, end: { hour: 15, minute: 30 } },
    { start: { hour: 15, minute: 30 }, end: { hour: 16, minute: 0 } },
    { start: { hour: 16, minute: 0 }, end: { hour: 16, minute: 30 } },
    { start: { hour: 16, minute: 30 }, end: { hour: 17, minute: 0 } },
    { start: { hour: 17, minute: 0 }, end: { hour: 17, minute: 30 } },
    { start: { hour: 17, minute: 30 }, end: { hour: 18, minute: 0 } },
    { start: { hour: 18, minute: 0 }, end: { hour: 18, minute: 30 } },
    { start: { hour: 18, minute: 30 }, end: { hour: 19, minute: 0 } },
    { start: { hour: 19, minute: 0 }, end: { hour: 19, minute: 30 } },
    { start: { hour: 19, minute: 30 }, end: { hour: 20, minute: 0 } },
  ];

  const [custID, setCustID] = useState();

  const handleSubmit = async (e) => {
    e.preventDefault(); // Prevent default form submission

    if (selectedAppointment === null || selectedAppointment === undefined) {
      alert("No appointment selected. Please try again.");
      return;
    }

    // Ensure newTime is properly formatted (convert to 24-hour format if needed)
    if (!newTime) {
      alert("Please select a valid time.");
      return;
    }

    // Convert newTime (e.g., "1:30 PM") into 24-hour format (e.g., "13:30:00")
    const formattedTime = convertTo24HourFormat(newTime);

    // Format SchedDate to match "YYYY-MM-DDTHH:mm:ss"
    const schedDateTime = `${newDate}T${formattedTime}`;

    const requestBody = {
      UID: buid,
      BUID: "", // Keeping it empty as per API requirements
      FSMasterID: userID, // Assuming FSMasterID is EMP_ID
      CustomerID: custID,
      SchedDate: schedDateTime,
    };

    console.log("Request Body:", requestBody);

    try {
      const response = await fetch(
        "https://xianhefairy.com/LotusFairy_API/api/member/PostBookingSchedule?invite=1",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        throw new Error(
          `Failed to update appointment. Status: ${response.status}`
        );
      }

      const result = await response.json();
      console.log("Appointment updated successfully:", result);

      alert("Appointment updated successfully!");
      handleModalClose();
    } catch (error) {
      console.error("Error updating appointment:", error);
      alert("Failed to update appointment. Please try again.");
    }
  };

  const handleMeetNow = (custID) => {
    // Resolve the appointment's master info from profiles or legacy mapping
    const resolveMasterForAppointment = (empId) => {
      const numericEmp = Number(empId);
      const matched = profiles.find((p) => Number(p.ID) === numericEmp || p.EmpCode === `MAS${numericEmp - 7}`);
      if (matched) return { channelName: matched.DisplayName || matched.FirstName || "", masCode: matched.EmpCode || "" };

      // legacy fallbacks for known emp ids
      if (empId == 8) return { channelName: "Master Siewli", masCode: "MAS1" };
      if (empId == 9) return { channelName: "Master Dennis", masCode: "MAS2" };
      if (empId == 10) return { channelName: "Master Joey", masCode: "MAS3" };
      return { channelName, masCode };
    };

    const group = selectedGroup;

    // Determine master from the first appointment in group (or fallback)
    const first = (group?.items && group.items[0]) || null;
    const empId = first?.EMP_ID || first?.EMPID || first?.EMP_ID;
    const master = resolveMasterForAppointment(empId);

    const participants = Array.isArray(group?.items)
      ? group.items
          .map((a) => ({ custID: a?.CUST_ID, bookingUid: a?.UID }))
          .filter((p) => p.custID)
      : [];

    // Master's personal Zoom session is keyed off their display name/code so
    // every customer meeting them joins the same room.
    const zoomSessionName = master.channelName || master.masCode || "meeting";
    // Carried through so ZoomAppointment.jsx can mark the booking "live" via
    // PostBooking — that's the record the guest's waiting lobby polls for
    // via Booking/getCall.
    const zoomBookingParticipants =
      participants.length > 0 ? participants : custID ? [{ custID, bookingUid: buid }] : [];
    const zoomBookingDate = participants.length > 0 ? first?.REC_DATE || recDate : recDate;
    const zoomNavState = {
      sessionName: zoomSessionName,
      isHost: true,
      userName: master.channelName || master.masCode || "Master",
      masCode: master.masCode,
      bookingParticipants: zoomBookingParticipants,
      bookingDate: zoomBookingDate,
    };

    // If we have multiple participants in the same timeslot, pass them through
    if (participants.length > 1) {
      navigate("/appointment", { state: zoomNavState });
      localStorage.setItem("Cust_ID", String(participants[0].custID));
      return;
    }

    navigate("/appointment", { state: zoomNavState });
    localStorage.setItem("Cust_ID", custID);
    console.log("Data passed:", { custID, buid, recDate });
  };

  const handleOpenModal = () => {
    setIsModalVisible(true);
    // Reset modal state to avoid stale selections
    setSearchQuery("");
    setSelectedUser(null);
    setSelectedDate("");
    setSelectedTime("");
    setIsConfirmed(false);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setSearchQuery("");
    setSelectedUser(null);
    setSelectedDate("");
    setSelectedTime("");
    setIsConfirmed(false);
  };

  const [apiUsers, setApiUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [visibleUsers, setVisibleUsers] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isConfirmed, setIsConfirmed] = React.useState(false);
  const [selectedDate, setSelectedDate] = React.useState("");
  const [selectedTime, setSelectedTime] = React.useState("");
  const [selectedUser, setSelectedUser] = React.useState(null);
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddingMeeting, setIsAddingMeeting] = useState(false);

  const filteredConsultations = React.useMemo(() => {
    try {
      return (consultations || []).filter((consult) => {
        if (!consult || !consult.REC_DATE) return false;
        const d = new Date(consult.REC_DATE);
        if (isNaN(d)) return false;
        const dateKey = d.toISOString().slice(0, 10);
        if (filterDate && dateKey !== filterDate) return false;
        if (timeFilter) {
          const timeKey = `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
          if (timeKey !== timeFilter) return false;
        }
        return true;
      });
    } catch (e) {
      return [];
    }
  }, [consultations, filterDate, timeFilter]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        // Dynamically build the API URL based on searchQuery
        const url = searchQuery
          ? `https://xianhefairy.com/LotusFairy_API/api/Booking/getUser?username=${searchQuery}`
          : "https://xianhefairy.com/LotusFairy_API/api/Booking/getUser"; // Default URL if no searchQuery

        const response = await fetch(url);
        const data = await response.json();
        setApiUsers(data);
        setVisibleUsers(data.slice(0, 10));
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };

    fetchUsers();
  }, [searchQuery]);

  useEffect(() => {
    const fetchAppointments = async () => {
      try {
        const response = await fetch(
          `https://xianhefairy.com/LotusFairy_API/api/booking/getMasterRec?ID=${userID}`
        );
        const data = await response.json();
        setConsultations(data);
      } catch (error) {
        console.error("Error fetching consultations:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAppointments();
  }, [userID]);

  const handleSearch = (event) => {
    const query = event.target.value.toLowerCase();
    setSearchQuery(query);

    const filteredUsers = apiUsers.filter((user) =>
      user.NAME.toLowerCase().includes(query)
    );

    setVisibleUsers(filteredUsers.slice(0, 10));
  };

  const handleUserSelect = async (user) => {
    if (isAddingMeeting) return;
    if (!user || !selectedDate || !selectedTime) {
      alert("Please select a user, date, and time.");
      return;
    }

    const schedDateTime = buildSchedDateTime(selectedDate, selectedTime);
    if (!schedDateTime) {
      alert("Please select a valid date and time.");
      return;
    }

    const envelope = buildPostBookingScheduleEnvelope({
      masterId: userID,
      customerId: user.CUST_ID,
      schedDateTime,
      updateBy: localStorage.getItem("username") || "",
    });

    try {
      setIsAddingMeeting(true);
      const response = await fetch(
        "https://xianhefairy.com/LotusFairy_API/api/member/PostBookingSchedule?invite=1",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(envelope),
        }
      );

      let data;
      try {
        data = await response.json();
      } catch (e) {
        data = null;
      }

      if (!response.ok) {
        const msg =
          (data && (data.Msg || data.message || data.error)) ||
          `Failed to add meeting (HTTP ${response.status})`;
        console.error("Reservation failed", { status: response.status, data });
        alert(msg);
        return;
      }

      console.log("Reservation successful", data);
      handleCloseModal();
      // Refresh to re-fetch and show the newly added meeting
      window.location.reload();
    } catch (error) {
      console.error("Error posting reservation:", error);
      alert("Network error while adding meeting. Please try again.");
    } finally {
      setIsAddingMeeting(false);
    }
  };

  const handleDownload = async (BUID, fileName) => {
    let urlEndpoint = "";
    try {
      if (!BUID) {
        console.error("handleDownload called without a BUID");
        alert("No recording identifier available for download.");
        return;
      }

      urlEndpoint = `https://xianhefairy.com/LotusFairy_API/api/video/getRecording?vuid=${encodeURIComponent(
        BUID
      )}${fileName ? `&fileName=${encodeURIComponent(fileName)}` : ""}`;
      console.log("Downloading recording from:", urlEndpoint);

      // First attempt: fetch as binary without forcing credentials (avoids CORS preflight)
      let response;
      try {
        response = await fetch(urlEndpoint, {
          method: "GET",
        });
      } catch (fetchErr) {
        console.warn("Fetch failed (possible CORS/network). Falling back to direct navigation:", fetchErr);
        // Fallback: let the browser open the URL directly so it can handle the download
        try {
          window.open(urlEndpoint, "_blank");
        } catch (openErr) {
          console.error("Fallback window.open failed:", openErr);
          alert("Unable to start download. Check console for details and verify server CORS settings.");
        }
        return;
      }

      console.log("Download response status:", response.status, response.statusText);

      const contentType = response.headers.get("content-type") || "";
      if (!response.ok) {
        if (contentType.includes("application/json")) {
          const errJson = await response.json();
          console.error("Server returned JSON error:", errJson);
          alert(errJson?.message || JSON.stringify(errJson));
          return;
        }
        throw new Error(`Failed to fetch recording: ${response.status}`);
      }

      if (contentType.includes("application/json")) {
        const data = await response.json();
        console.warn("Expected binary but received JSON:", data);
        if (data?.url) {
          window.open(data.url, "_blank");
          return;
        }
        alert("Server returned JSON instead of a video. Check console for details.");
        return;
      }

      // Otherwise, assume binary and download
      const blob = await response.blob();
      const element = document.createElement("a");
      const blobUrl = URL.createObjectURL(blob);
      element.href = blobUrl;
      element.download = fileName || `${BUID}_appointment.mp4`;
      element.style.display = "none";
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Error downloading file:", error);
      // Last-resort fallback: try to open the direct URL
      if (urlEndpoint) {
        try {
          window.open(urlEndpoint, "_blank");
        } catch (e) {
          console.error("Fallback open URL failed:", e);
        }
      }
      alert("Error downloading recording. Check the console for details.");
    }
  };

  const displayedAppointments = (appointments || []).filter((appointment) => {
    if (!appointment || !appointment.REC_DATE) return false;
    try {
      const apptDate = new Date(appointment.REC_DATE);
      const apptDateStr = getLocalDateKey(apptDate);
      return apptDateStr === filterDate;
    } catch (e) {
      return false;
    }
  });

  const groupedAppointments = React.useMemo(() => {
    const groups = new Map();
    for (const appt of displayedAppointments) {
      try {
        const d = new Date(appt.REC_DATE);
        const key = getLocalDateTimeKeyMinute(d);
        if (!key) continue;
        if (!groups.has(key)) {
          groups.set(key, { key, date: d, items: [] });
        }
        groups.get(key).items.push(appt);
      } catch (e) {
        // ignore invalid dates
      }
    }
    return Array.from(groups.values()).sort((a, b) => {
      try {
        return new Date(a.items?.[0]?.REC_DATE).getTime() - new Date(b.items?.[0]?.REC_DATE).getTime();
      } catch (e) {
        return 0;
      }
    });
  }, [displayedAppointments]);

  const getMemberLabel = (appt) => {
    if (!appt) return "";
    return (
      appt.NAME ||
      appt.CUST_NAME ||
      appt.EMAIL ||
      (appt.CUST_ID ? `Customer ID: ${appt.CUST_ID}` : "") ||
      (appt.UID ? `UID: ${appt.UID}` : "")
    );
  };

  return (
    <div style={styles.container}>
      <style>
        {`@keyframes xf-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}
      </style>
      <h1 style={{ ...styles.header, marginBottom: isNarrowScreen ? "18px" : "40px" }}>Appointments</h1>
      <div
        style={{
          display: "flex",
          alignItems: isNarrowScreen ? "stretch" : "center",
          gap: "12px",
          justifyContent: "center",
          margin: isNarrowScreen ? "0 0 12px" : "2%",
          flexDirection: isNarrowScreen ? "column" : "row",
        }}
      >
        <button
          onClick={handleOpenModal}
          style={{
            padding: isNarrowScreen ? "8px 14px" : "10px 20px",
            backgroundColor: "blue",
            color: "#fff",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            width: isNarrowScreen ? "100%" : "auto",
          }}
        >
          Add meeting
        </button>

        <label style={{ display: "flex", alignItems: isNarrowScreen ? "flex-start" : "center", gap: "8px", fontFamily: "Arial, sans-serif", flexDirection: isNarrowScreen ? "column" : "row", width: isNarrowScreen ? "100%" : "auto" }}>
          <span style={{ color: "#333" }}>Appointment Date:</span>
          <input
            type="date"
            value={filterDate}
            min={todayDateString}
            onChange={(e) => setFilterDate(e.target.value)}
            style={{ padding: "8px", borderRadius: "6px", border: "1px solid #ccc", width: isNarrowScreen ? "100%" : "auto", boxSizing: "border-box" }}
          />
        </label>

        <label style={{ display: "flex", alignItems: isNarrowScreen ? "flex-start" : "center", gap: "8px", fontFamily: "Arial, sans-serif", flexDirection: isNarrowScreen ? "column" : "row", width: isNarrowScreen ? "100%" : "auto" }}>
          <span style={{ color: "#333" }}>Time:</span>
          <select
            value={timeFilter}
            onChange={(e) => setTimeFilter(e.target.value)}
            style={{ padding: "8px", borderRadius: "6px", border: "1px solid #ccc", background: "#fff", width: isNarrowScreen ? "100%" : "auto", boxSizing: "border-box" }}
          >
            <option value="">All times</option>
            {generateHalfHourTimes(10, 20).map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </label>
      </div>

      {isModalVisible && (
        <div style={overlayStyles} onClick={handleCloseModal}>
          <div style={modalStyles} onClick={(e) => e.stopPropagation()}>
            <div style={headerStyles}>
              <h2 style={titleStyles}>Search User</h2>
              <input
                type="text"
                placeholder="Search by name..."
                value={searchQuery}
                onChange={handleSearch}
                style={inputStyles}
              />
            </div>
            <div style={{ marginBottom: "1rem" }}>
              <label>
                Select Date:
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  min={todayDateString}
                  style={{ marginLeft: "0.5rem" }}
                />
              </label>
            </div>
            <div style={{ marginBottom: "1rem" }}>
              <label>
                Select Time:
                <select
                  value={selectedTime}
                  onChange={(e) => setSelectedTime(e.target.value)}
                  style={{
                    marginLeft: "0.5rem",
                    padding: "0.2rem 0.4rem", // Adjusted padding to reduce height
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                    backgroundColor: "#f9f9f9",
                    fontSize: "14px",
                    fontFamily: "Arial, sans-serif",
                    height: "30px", // Set a fixed height to make it smaller
                  }}
                >
                  <option value="" disabled>
                    Select time...
                  </option>
                  {generateHalfHourTimes(10, 20).map((formattedTime) => (
                    <option key={formattedTime} value={formattedTime}>
                      {formattedTime}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div style={scrollViewStyles}>
              <ul style={listStyles}>
                {visibleUsers.map((user) => (
                  <li
                    key={user.USER_ID}
                    style={{
                      ...itemStyles,
                      backgroundColor:
                        selectedUser?.USER_ID === user.USER_ID
                          ? "#d3f9d8"
                          : "#fff",
                      cursor: isAddingMeeting ? "not-allowed" : "pointer",
                      opacity: isAddingMeeting ? 0.7 : 1,
                    }}
                    onClick={() => {
                      if (isAddingMeeting) return;
                      setSelectedUser(user);
                    }}
                  >
                    {user.NAME} - {user.EMAIL}
                  </li>
                ))}
              </ul>
            </div>
            <div style={{ marginBottom: "1rem" }}>
              <label>
                <input
                  type="checkbox"
                  checked={isConfirmed}
                  onChange={() => setIsConfirmed(!isConfirmed)}
                  style={{ marginRight: "0.5rem" }}
                />
                Are you sure?
              </label>
            </div>
            <button
              onClick={() => handleUserSelect(selectedUser)}
              style={{
                ...closeButtonStyles,
                opacity:
                  !isAddingMeeting &&
                  isConfirmed &&
                  selectedUser &&
                  selectedDate &&
                  selectedTime
                    ? 1
                    : 0.5,
                pointerEvents:
                  !isAddingMeeting &&
                  isConfirmed &&
                  selectedUser &&
                  selectedDate &&
                  selectedTime
                    ? "auto"
                    : "none",
                backgroundColor: "blue",
              }}
            >
              {isAddingMeeting ? (
                <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
                  <span
                    style={{
                      width: "14px",
                      height: "14px",
                      borderRadius: "999px",
                      border: "2px solid rgba(255, 255, 255, 0.4)",
                      borderTop: "2px solid rgba(255, 255, 255, 1)",
                      animation: "xf-spin 0.8s linear infinite",
                    }}
                  />
                  Adding...
                </span>
              ) : (
                "Confirm"
              )}
            </button>
            <button onClick={handleCloseModal} style={closeButtonStyles}>
              Close
            </button>
          </div>
        </div>
      )}

      <ul style={styles.list}>
        {groupedAppointments.length === 0 ? (
          <li style={{ textAlign: "center", padding: "20px", color: "#666" }}>
            No appointments currently for selected date.
          </li>
        ) : (
          groupedAppointments.map((group) => {
            const first = (group.items && group.items[0]) || {};
            const recDateObj = first.REC_DATE ? new Date(first.REC_DATE) : null;
            const formattedDate = recDateObj
              ? recDateObj.toLocaleDateString()
              : "";
            const formattedTime = recDateObj
              ? recDateObj.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
              : "";

            const isSelected = selectedGroupKey === group.key;
            const isGroupMeeting = (group.items || []).length > 1;

            const memberLabels = (group.items || [])
              .map(getMemberLabel)
              .filter(Boolean);
            const memberPreview =
              memberLabels.length <= 2
                ? memberLabels.join(", ")
                : `${memberLabels.slice(0, 2).join(", ")} +${memberLabels.length - 2} more`;

            // Past consultations that match any participant in this appointment group AND the same time slot
            const matchingConsults = (filteredConsultations || []).filter((consult) => {
              try {
                // Only include recordings whose local date+time slot matches this appointment group
                const consultTimeKey = getLocalDateTimeKeyMinute(new Date(consult.REC_DATE));
                if (consultTimeKey !== group.key) return false;
                return (group.items || []).some((it) => {
                  if (!it || !consult) return false;
                  if (it.CUST_ID && consult.CUST_ID && String(it.CUST_ID) === String(consult.CUST_ID)) return true;
                  if (it.UID && consult.UID && String(it.UID) === String(consult.UID)) return true;
                  if (it.BUID && consult.BUID && String(it.BUID) === String(consult.BUID)) return true;
                  return false;
                });
              } catch (e) {
                return false;
              }
            });

            // Build a deduplicated list of matching consultations so files are only shown once
            const uniqMatchingConsults = (() => {
              const uniq = [];
              const seen = new Set();
              for (const consult of matchingConsults) {
                const key = String(consult.BUID || consult.UID || (Array.isArray(consult.FILENAME) ? consult.FILENAME.join('|') : consult.REC_DATE || '') || Math.random());
                if (!seen.has(key)) {
                  seen.add(key);
                  uniq.push(consult);
                }
              }
              return uniq;
            })();
            // Keep only consultations that actually have filenames for the Past Consultations list
            const pastWithFiles = uniqMatchingConsults.filter(
              (c) => Array.isArray(c.FILENAME) && c.FILENAME.length > 0
            );

            return (
              <li key={group.key} style={styles.listItem}>
                <button
                  style={{
                    ...styles.button,
                    ...(isSelected ? styles.selectedButton : {}),
                  }}
                  onClick={() => handleAppointmentClick(group)}
                >
                  <div style={styles.dateTime}>
                    <strong>Date:</strong>{" "}
                    {formattedDate} |{" "}
                    <strong>Time:</strong>{" "}
                    {formattedTime}
                  </div>
                  <div style={styles.description}>
                    {isGroupMeeting ? (
                      <>
                        <strong>Group meeting:</strong> {group.items.length} members
                        {memberPreview ? (
                          <div style={{ marginTop: "6px" }}>
                            <strong>Members:</strong> {memberPreview}
                          </div>
                        ) : null}
                      </>
                    ) : (
                      <>
                        <strong>Email:</strong> {first.EMAIL}
                      </>
                    )}
                  </div>
                </button>
                {isSelected && (
                  <>
                    <div style={styles.actionButtonsContainer}>
                      <button
                        style={styles.actionButton}
                        onClick={() => handleChangeAppointment(first)}
                      >
                        Change Appointment
                      </button>
                      <button
                        style={styles.meetButton}
                        onClick={() => handleMeetNow(first.CUST_ID)}
                      >
                        Meet Now
                      </button>
                    </div>

                    {/* Recordings (single or group) */}
                    {group.items && group.items.some((it) => it.BUID) && (
                      <div
                        style={{
                          marginTop: "10px",
                          padding: "10px",
                          border: "1px solid #e5e7eb",
                          borderRadius: "8px",
                          backgroundColor: "#fafafa",
                        }}
                      >
                        <div style={{ fontWeight: "bold", marginBottom: "6px" }}>
                          Recordings
                        </div>
                        <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                          {group.items.map((it, idx) =>
                            it && it.BUID ? (
                              <li
                                key={`rec-${group.key}-${idx}`}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  padding: "6px 0",
                                }}
                              >
                                <span style={{ marginRight: "8px" }}>{getMemberLabel(it)}</span>
                                <span style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                                  {(() => {
                                    // If this recording appears in the deduped past consultations, skip rendering here
                                    const isInPast = uniqMatchingConsults.some((c) =>
                                      (it.BUID && c.BUID && String(c.BUID) === String(it.BUID)) ||
                                      (it.UID && c.UID && String(c.UID) === String(it.UID))
                                    );
                                    if (isInPast) return null;

                                    const matchedConsult = (consultations || []).find(
                                      (c) =>
                                        (it.BUID && c.BUID && String(c.BUID) === String(it.BUID)) ||
                                        (it.UID && c.UID && String(c.UID) === String(it.UID))
                                    );
                                    const fileNames =
                                      matchedConsult && Array.isArray(matchedConsult.FILENAME) && matchedConsult.FILENAME.length > 0
                                        ? matchedConsult.FILENAME
                                        : [null];
                                    return fileNames.map((fn, fnIdx) => (
                                      <button
                                        key={fnIdx}
                                        style={{
                                          ...styles.downloadButton,
                                          padding: "6px 10px",
                                          fontSize: "14px",
                                        }}
                                        onClick={() => handleDownload(it.BUID, fn)}
                                      >
                                        {fn ? `Download ${fn}` : "Download"}
                                      </button>
                                    ));
                                  })()}
                                </span>
                              </li>
                            ) : null
                          )}
                        </ul>
                      </div>
                    )}

                    {isGroupMeeting && memberLabels.length > 0 && (
                      <div
                        style={{
                          marginTop: "10px",
                          padding: "10px",
                          border: "1px solid #e5e7eb",
                          borderRadius: "10px",
                          backgroundColor: "#fff",
                          fontFamily: "Arial, sans-serif",
                        }}
                      >
                        <div style={{ fontWeight: "bold", marginBottom: "6px" }}>
                          Members in this slot
                        </div>
                        <ul style={{ margin: 0, paddingLeft: "18px" }}>
                          {memberLabels.map((label, idx) => (
                            <li key={`${group.key}-${idx}`}>{label}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {pastWithFiles.length > 0 && (
                      <div style={{ marginTop: "12px", padding: "10px", border: "1px solid #e6ffed", borderRadius: "8px", backgroundColor: "#f7fffb" }}>
                        <div style={{ fontWeight: "bold", marginBottom: "6px" }}>Past Consultations with Recording</div>
                        <ul style={{ margin: 0, padding: 0 }}>
                          {pastWithFiles.map((consult) => {
                            const recDate = new Date(consult.REC_DATE);
                            const formattedDate = isNaN(recDate) ? "" : recDate.toLocaleDateString();
                            const formattedTime = isNaN(recDate) ? "" : recDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
                            return (
                              <li key={`past-${consult.UID || consult.BUID || Math.random()}`} style={{ marginBottom: "8px" }}>
                                <strong>Date:</strong> {formattedDate} <strong>Time:</strong> {formattedTime}
                                <br />
                                <span style={{ display: "flex", flexDirection: "column", gap: "4px", marginTop: "6px" }}>
                                  {consult.FILENAME.map((fn, fnIdx) => (
                                    <button
                                      key={fnIdx}
                                      style={{
                                        padding: "6px 12px",
                                        backgroundColor: "#4CAF50",
                                        color: "white",
                                        border: "none",
                                        borderRadius: "4px",
                                        cursor: "pointer",
                                        width: "40%",
                                      }}
                                      onClick={() => handleDownload(consult.BUID, fn)}
                                    >
                                      Download {fn}
                                    </button>
                                  ))}
                                </span>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                  </>
                )}
              </li>
            );
          })
        )}

        {/* Past consultations are shown per-appointment in the expanded area */}
      </ul>

      {isModalOpen && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <h2>Change Appointment</h2>
            <form onSubmit={handleSubmit}>
              <label>
                New Date:
                <input
                  type="date"
                  value={newDate}
                  onChange={(e) => setNewDate(e.target.value)}
                  required
                  style={styles.input}
                  min={new Date().toISOString().split("T")[0]} // Disable past dates
                />
              </label>
              <br />
              <label>
                New Time:
                <div style={{ position: "relative" }}>
                  <select
                    value={newTime}
                    onChange={(e) => {
                      console.log("New time selected:", e.target.value);
                      setNewTime(e.target.value);
                    }}
                    required
                    style={styles.input}
                  >
                    {timeSlots.map((slot, index) => {
                      const startHour = slot.start.hour % 12 || 12;
                      const endHour = slot.end.hour % 12 || 12;
                      const startPeriod = slot.start.hour >= 12 ? "PM" : "AM";
                      const endPeriod = slot.end.hour >= 12 ? "PM" : "AM";
                      const startTime = `${startHour}:${slot.start.minute
                        .toString()
                        .padStart(2, "0")} ${startPeriod}`;
                      const endTime = `${endHour}:${slot.end.minute
                        .toString()
                        .padStart(2, "0")} ${endPeriod}`;
                      return (
                        <option key={index} value={startTime}>
                          {startTime} - {endTime}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </label>
              <br />
              <button type="submit" style={styles.submitButton}>
                Submit
              </button>
              <button
                type="button"
                style={styles.cancelButton}
                onClick={handleModalClose}
              >
                Cancel
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    maxWidth: "95vw",
    width: "100%",
    margin: "0 auto",
    padding: "30px",
    backgroundColor: "#fff",
    borderRadius: "12px",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
    boxSizing: "border-box",
    overflowX: "hidden",
  },
  header: {
    textAlign: "center",
    color: "#333",
    fontSize: "clamp(1.6rem, 4vw, 2.5rem)",
    fontWeight: "bold",
    marginBottom: "40px",
    fontFamily: "Arial, sans-serif",
  },
  list: {
    listStyleType: "none",
    padding: 0,
  },
  listItem: {
    marginBottom: "15px",
    minWidth: 0,
  },
  button: {
    width: "100%",
    padding: "20px",
    backgroundColor: "#f0f0f0",
    color: "#333",
    border: "1px solid #ddd",
    borderRadius: "10px",
    cursor: "pointer",
    textAlign: "left",
    transition: "all 0.3s ease",
    boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)",
    fontFamily: "Arial, sans-serif",
    boxSizing: "border-box",
    wordBreak: "break-word",
  },
  selectedButton: {
    backgroundColor: "#e0e0e0",
    borderColor: "#007bff",
  },
  actionButtonsContainer: {
    display: "flex",
    justifyContent: "space-between",
    gap: "10px",
    flexWrap: "wrap",
    marginTop: "10px",
  },
  actionButton: {
    backgroundColor: "#007bff",
    color: "#fff",
    padding: "10px 20px",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "background-color 0.3s ease",
    fontFamily: "Arial, sans-serif",
    flex: "1 1 220px",
    minWidth: 0,
    fontSize: "16px",
    boxSizing: "border-box",
  },
  meetButton: {
    backgroundColor: "#28a745",
    color: "#fff",
    padding: "10px 20px",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    flex: "1 1 220px",
    minWidth: 0,
    fontSize: "16px",
    boxSizing: "border-box",
  },
  downloadButton: {
    backgroundColor: "#6c757d",
    color: "#fff",
    padding: "10px 20px",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "16px",
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 3,
  },
  modalContent: {
    backgroundColor: "#fff",
    padding: "30px",
    borderRadius: "12px",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.2)",
    width: "min(400px, 92vw)",
    minWidth: "0",
    maxWidth: "92vw",
    zIndex: 4,
    alignContent: "center",
    boxSizing: "border-box",
  },
  submitButton: {
    backgroundColor: "#4CAF50",
    color: "#fff",
    padding: "10px 20px",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    marginRight: "10px",
  },
  cancelButton: {
    backgroundColor: "#f44336",
    color: "#fff",
    padding: "10px 20px",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
  },
  input: {
    padding: "8px",
    borderRadius: "6px",
    border: "1px solid #ccc",
    marginBottom: "10px",
  },
  dateTime: {
    marginBottom: "8px",
    fontSize: "1.2rem",
    fontWeight: "bold",
  },
  description: {
    color: "#555",
    fontSize: "1rem",
  },
};

const overlayStyles = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 200,
};

const modalStyles = {
  display: "flex",
  flexDirection: "column",
  padding: "20px",
  backgroundColor: "#fff",
  borderRadius: "8px",
  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
  width: "min(400px, 92vw)",
  maxWidth: "92vw",
  height: "80%",
  margin: "0 auto",
  boxSizing: "border-box",
};

const headerStyles = {
  marginBottom: "20px",
};

const titleStyles = {
  margin: "0 0 10px",
  fontSize: "18px",
  fontWeight: "bold",
};

const inputStyles = {
  width: "100%",
  padding: "10px",
  borderRadius: "4px",
  border: "1px solid #ccc",
  fontSize: "14px",
};

const listStyles = {
  listStyleType: "none",
  padding: 0,
  margin: 0,
  height: "100%",
};

const itemStyles = {
  padding: "10px",
  borderBottom: "1px solid #eee",
};

const buttonStyles = {
  marginTop: "10px",
  padding: "10px 20px",
  backgroundColor: "#007BFF",
  color: "#fff",
  border: "none",
  borderRadius: "4px",
  cursor: "pointer",
};

const closeButtonStyles = {
  marginTop: "5px",
  padding: "10px 20px",
  backgroundColor: "#DC3545",
  color: "#fff",
  border: "none",
  borderRadius: "4px",
  cursor: "pointer",
};

const scrollViewStyles = {
  height: "90%",
  overflowY: "auto",
  marginBottom: "10px",
};

export default AppointmentPage;
